// Cart changes from these sources already render the drawer themselves.
const NCM_SELF_RENDERING_SOURCES = new Set(['product-form', 'cart-drawer', 'ncm-fbt', 'ncm-upsell']);

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.queue = Promise.resolve();
    this.upsellCache = new Map();
    this.pricingCount = 0;
    this.renderSeq = 0;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('submit', (event) => {
      if (this.pricingCount > 0 && event.target.closest('#CartDrawer-Form')) event.preventDefault();
    });
    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-ncm-protection-input]')) return;
      if (this.pricingCount > 0) {
        event.target.checked = !event.target.checked;
        return;
      }
      this.toggleProtection(event.target);
    });
    this.prepareCartIcon();
  }

  get config() {
    return window.NcmBundle?.config || {};
  }

  get sectionIds() {
    return this.getSectionsToRender().map((section) => section.id);
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  nextSeq() {
    this.renderSeq += 1;
    return this.renderSeq;
  }

  isCurrentSeq(seq) {
    return seq == null || seq === this.renderSeq;
  }

  lockCheckout(lock) {
    const button = this.querySelector('#CartDrawer-Checkout');
    if (!button) return;
    button.disabled = lock;
    button.setAttribute('aria-busy', String(lock));
    if (lock) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
  }

  beginPricing({ announce } = {}) {
    this.pricingCount += 1;
    this.lockCheckout(true);
    window.clearTimeout(this.pricingDelay);
    const count = this.pricingCount;
    this.pricingDelay = window.setTimeout(() => {
      if (this.pricingCount !== count) return;
      this.classList.add('is-pricing');
      this.classList.remove('is-price-ready');
      this.setAttribute('aria-busy', 'true');
    }, 80);
    const status = this.querySelector('#CartDrawer-LiveRegionText');
    if (status && announce) status.textContent = announce;
  }

  endPricing() {
    this.pricingCount = Math.max(0, this.pricingCount - 1);
    if (this.pricingCount > 0) return;
    window.clearTimeout(this.pricingDelay);
    const shown = this.classList.contains('is-pricing');
    this.classList.remove('is-pricing');
    this.removeAttribute('aria-busy');
    this.lockCheckout(false);
    if (!shown || this.prefersReducedMotion()) return;
    this.classList.add('is-price-ready');
    window.clearTimeout(this.revealTimer);
    this.revealTimer = window.setTimeout(() => this.classList.remove('is-price-ready'), 240);
  }

  prepareCartIcon() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  onClick(event) {
    if (event.target.closest('#CartDrawer-Overlay, [data-ncm-drawer-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    const toggle = event.target.closest('[data-ncm-upsell-toggle]');
    if (toggle) return this.toggleUpsell(toggle);
    const value = event.target.closest('.ncm-upsell__value');
    if (value) return this.chooseUpsellValue(value);
    const add = event.target.closest('[data-ncm-upsell-add]');
    if (add) return this.addUpsell(add);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const alreadyOpen = this.classList.contains('active');
    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('ncm-drawer-open');
    if (!alreadyOpen) window.NcmScrollLock?.lock();

    if (alreadyOpen) {
      this.trap();
      return;
    }

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => this.classList.add('animate', 'active'));

    let trapped = false;
    const trapOnce = () => {
      if (trapped || !this.classList.contains('active')) return;
      trapped = true;
      this.trap();
    };
    this.querySelector('.drawer__inner')?.addEventListener('transitionend', trapOnce, { once: true });
    setTimeout(trapOnce, 450);
  }

  // The cart may have changed elsewhere (another tab, /cart), so the drawer re-reads it when opened.
  openFromIcon(cartLink) {
    this.open(cartLink);
    this.enqueue(() => this.refresh());
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('ncm-drawer-open');
    window.NcmScrollLock?.unlock();
  }

  trap(elementToFocus) {
    const container = document.getElementById('CartDrawer');
    if (container) trapFocus(container, elementToFocus || this.querySelector('.drawer__inner'));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /*
    Called with a cart API response that includes `sections`. Product forms pass the added line item;
    other callers pass `added` (lines to highlight; [] for none) and `open`.
  */
  renderContents(parsedState, options = {}) {
    const {
      open = this.config.autoOpen !== false,
      added = parsedState.key ? [parsedState] : [],
      focus = null,
      fromSync = false,
      seq,
    } = options;
    if (!this.isCurrentSeq(seq)) return;
    this.productId = parsedState.id;

    const scrollTop = this.querySelector('[data-ncm-drawer-scroll]')?.scrollTop || 0;

    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections?.[section.id];
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const inner = html ? this.getSectionInnerHTML(html, section.selector) : '';
      if (!inner || !sectionElement) return;
      sectionElement.innerHTML = inner;
    });

    const inner = this.querySelector('.drawer__inner');
    this.classList.toggle('is-empty', Number(inner?.dataset.cartCount || 0) === 0);
    if (this.pricingCount > 0) this.lockCheckout(true);

    if (added.length) {
      this.lastAdded = {
        keys: added.map((item) => item.key),
        variantIds: added.map((item) => Number(item.variant_id || item.id)),
        at: Date.now(),
      };
    }
    const highlighted = this.highlightAdded(added.length || fromSync ? this.lastAdded : null);
    const scroller = this.querySelector('[data-ncm-drawer-scroll]');
    if (highlighted.length) {
      highlighted[0].scrollIntoView({ block: 'nearest' });
    } else if (scroller) {
      scroller.scrollTop = scrollTop;
    }

    if (added.length) {
      const titles = highlighted.map((line) => line.getAttribute('aria-label')).join(', ');
      const status = this.querySelector('#CartDrawer-LiveRegionText');
      if (status && titles) status.textContent = `${titles} added to cart`;
    }

    this.loadUpsells();

    if (open) {
      this.open();
    } else if (this.classList.contains('active')) {
      this.trap(focus || inner);
    }

    if (!fromSync) this.syncCart();
  }

  highlightAdded(recent) {
    if (!recent || Date.now() - recent.at > 4000) return [];
    const lines = [...this.querySelectorAll('.ncm-drawer-line')].filter(
      (line) =>
        recent.keys.includes(line.dataset.lineKey) || recent.variantIds.includes(Number(line.dataset.variantId))
    );
    lines.forEach((line) => line.classList.add('is-added'));
    return lines;
  }

  async refresh(options = {}) {
    try {
      const ids = this.sectionIds;
      const urls = [
        `${window.Shopify?.routes?.root || '/'}?sections=${ids.join(',')}`,
        `${routes.cart_url}?sections=${ids.join(',')}`,
      ];
      let sections = null;
      for (const url of urls) {
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) continue;
        const data = await response.json();
        if (data && ids.every((id) => typeof data[id] === 'string')) {
          sections = data;
          break;
        }
      }
      if (!sections) return false;
      this.renderContents({ sections }, { open: false, added: [], ...options });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  // Serializes cart requests made from the drawer so rapid clicks can't apply out of order.
  enqueue(task) {
    const run = this.queue.then(task, task);
    this.queue = run.catch(() => {});
    return run;
  }

  findLine(key, variantId) {
    const lines = [...this.querySelectorAll('.ncm-drawer-line')];
    return (
      lines.find((line) => line.dataset.lineKey === key) ||
      lines.find((line) => variantId && line.dataset.variantId === String(variantId)) ||
      null
    );
  }

  // Uses the line item key (looked up again when the request runs, since keys change with discounts).
  changeLine({ line, quantity, name, variantId }) {
    const original = document.getElementById(`CartDrawer-Item-${line}`);
    const key = original?.dataset.lineKey;
    variantId ||= original?.dataset.variantId;

    return this.enqueue(async () => {
      const lineElement = this.findLine(key, variantId);
      if (!lineElement) return;
      const id = lineElement.dataset.lineKey;
      const seq = this.nextSeq();
      this.setLineLoading(lineElement, true);
      this.beginPricing({ announce: 'Updating cart totals' });

      let state;
      try {
        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          cache: 'no-store',
          body: JSON.stringify({
            id,
            quantity,
            sections: this.sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        state = await response.json();
        if (!response.ok || state.errors || state.status) {
          throw new Error(state.description || state.errors || state.message || '');
        }
        this.renderContents(state, { open: false, added: [], seq });
      } catch (error) {
        this.setLineLoading(lineElement, false);
        const input = lineElement.querySelector('.quantity__input');
        if (input) input.value = input.getAttribute('value');
        this.showLineError(lineElement, error.message || window.cartStrings?.error);
        return;
      } finally {
        this.endPricing();
      }

      const updated = state.items.find((item) => item.key === id) || state.items.find((item) => String(item.variant_id) === String(variantId));
      const newLine = updated ? this.findLine(updated.key, updated.variant_id) : null;
      if (quantity > 0 && updated && updated.quantity !== quantity) {
        this.showLineError(newLine, window.cartStrings?.quantityError?.replace('[quantity]', updated.quantity));
      }

      const focusTarget =
        newLine?.querySelector(`[name="${name}"]`) ||
        this.querySelector('.ncm-drawer-line .cart-item__name') ||
        this.querySelector('.ncm-drawer__empty a');
      if (this.classList.contains('active')) this.trap(focusTarget || undefined);

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: state, variantId });
    });
  }

  setLineLoading(lineElement, loading) {
    lineElement?.classList.toggle('is-loading', loading);
    lineElement?.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
    lineElement?.setAttribute('aria-busy', String(loading));
    document.getElementById('CartDrawer-LineItemStatus')?.setAttribute('aria-hidden', String(!loading));
  }

  showLineError(lineElement, message) {
    const target = lineElement?.querySelector('.cart-item__error-text');
    if (target) {
      target.textContent = message || '';
    } else if (message) {
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = message;
    }
  }

  /*
    After every change (ncm-bundle.js): package protection stays at one unit and leaves with the last
    physical product; in discount code mode the right tier code goes on the cart.
  */
  syncCart() {
    const bundle = window.NcmBundle;
    if (!bundle) return;
    const inner = this.querySelector('.drawer__inner');
    const protectionQty = Number(inner?.dataset.protectionQty || 0);
    const fixProtection =
      protectionQty > 0 &&
      (protectionQty > 1 || Number(inner.dataset.protectionLines) > 1 || Number(inner.dataset.physicalCount) === 0);
    const syncCode = this.config.method === 'code';
    if (!fixProtection && !syncCode) return;

    this.enqueue(async () => {
      const seq = this.nextSeq();
      this.beginPricing();
      let state = null;
      try {
        if (fixProtection) state = await bundle.enforceProtection({ sections: this.sectionIds }).catch(() => null);
        if (syncCode) {
          const result = await bundle.syncCode({ cart: state || undefined, sections: this.sectionIds }).catch(() => null);
          if (result?.cart?.sections) state = result.cart;
        }
        if (!state?.sections) return;
        this.renderContents(state, { open: false, added: [], fromSync: true, seq });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: state });
      } finally {
        this.endPricing();
      }
    });
  }

  /*
    Adds or removes the real protection product. The switch shows the request's outcome only after
    Shopify answers: the drawer re-renders from the cart response, or from a fresh cart on failure.
  */
  toggleProtection(input) {
    const bundle = window.NcmBundle;
    const wrapper = input.closest('[data-ncm-protection]');
    const variantId = Number(wrapper?.dataset.variantId);
    const enable = input.checked;
    const title = wrapper?.querySelector('.ncm-protect__title')?.textContent.trim() || '';

    input.checked = !enable;
    input.disabled = true;
    wrapper?.classList.add('is-loading');
    wrapper?.setAttribute('aria-busy', 'true');
    this.beginPricing({ announce: 'Updating cart totals' });

    return this.enqueue(async () => {
      const seq = this.nextSeq();
      try {
        if (!bundle || !variantId) throw new Error();
        const cart = await bundle.fetchCart();
        const lines = cart.items.filter(bundle.isProtectionLine);

        if (enable && !lines.length) {
          await this.cartRequest(`${routes.cart_add_url}.js`, {
            items: [{ id: variantId, quantity: 1, properties: { _ncm_protection: 'Yes' } }],
          });
        } else if (enable) {
          await bundle.enforceProtection({ cart, sections: this.sectionIds });
        } else if (lines.length) {
          await this.cartRequest(`${routes.cart_update_url}.js`, {
            updates: Object.fromEntries(lines.map((line) => [line.key, 0])),
          });
        }

        const verified = await bundle.fetchCart();
        const on = verified.items.some(bundle.isProtectionLine);
        if (enable !== on) throw new Error();

        await this.refresh({ open: false, seq });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: verified });
      } catch (error) {
        if (!(await this.refresh({ open: false, seq })) && input.isConnected) {
          input.checked = !enable;
          input.disabled = false;
          wrapper.classList.remove('is-loading');
          wrapper.removeAttribute('aria-busy');
        }
        const message = error.message || this.config.protection?.error || window.cartStrings?.error || '';
        const errorElement = this.querySelector('[data-ncm-protection] .ncm-protect__error');
        if (errorElement && message) {
          errorElement.textContent = message;
          errorElement.hidden = false;
        }
        this.trap(this.querySelector('[data-ncm-protection-input]') || undefined);
        return;
      } finally {
        this.endPricing();
      }

      const status = this.querySelector('#CartDrawer-LiveRegionText');
      const on = Boolean(this.querySelector('[data-ncm-protection-input]')?.checked);
      if (status && title) status.textContent = `${title}: ${on ? 'added' : 'removed'}`;
      if (this.classList.contains('active')) this.trap(this.querySelector('[data-ncm-protection-input]') || undefined);
    });
  }

  async cartRequest(url, body) {
    const response = await fetch(url, {
      ...fetchConfig(),
      cache: 'no-store',
      body: JSON.stringify({ ...body, sections: this.sectionIds, sections_url: window.location.pathname }),
    });
    const state = await response.json();
    if (!response.ok || state.status) throw new Error(state.description || state.message || '');
    return state;
  }

  async loadUpsells() {
    const container = this.querySelector('[data-ncm-upsells]');
    const productId = container?.dataset.recommendFrom;
    if (!container || !productId) return;

    const inCart = [...this.querySelectorAll('.ncm-drawer-line')]
      .map((line) => line.dataset.productId)
      .sort()
      .join(',');
    const cacheKey = `${productId}|${inCart}`;
    let html = this.upsellCache.get(cacheKey);

    if (html === undefined) {
      try {
        const base = this.config.routes?.recommendations || '/recommendations/products';
        const response = await fetch(`${base}?section_id=ncm-cart-upsells&product_id=${productId}&limit=10&intent=related`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        html = doc.querySelector('[data-ncm-upsell-items]')?.innerHTML.trim() || '';
      } catch {
        html = '';
      }
      this.upsellCache.set(cacheKey, html);
    }

    if (!container.isConnected) return;
    const list = container.querySelector('[data-ncm-upsell-items]');
    if (html) {
      list.innerHTML = html;
      container.hidden = false;
    } else if (!list.children.length) {
      container.hidden = true;
    }
    container.removeAttribute('data-pending');
  }

  toggleUpsell(toggle) {
    const card = toggle.closest('[data-ncm-upsell]');
    const picker = card?.querySelector('.ncm-upsell__picker');
    if (!picker) return;
    const expand = toggle.getAttribute('aria-expanded') !== 'true';

    this.querySelectorAll('[data-ncm-upsell-toggle][aria-expanded="true"]').forEach((other) => {
      if (other === toggle) return;
      other.setAttribute('aria-expanded', 'false');
      other.closest('[data-ncm-upsell]').querySelector('.ncm-upsell__picker').hidden = true;
    });

    toggle.setAttribute('aria-expanded', String(expand));
    picker.hidden = !expand;
    if (!expand) return;

    this.initPicker(card);
    const { selected } = card.ncmPicker;
    const firstOpen = selected.findIndex((value) => value == null);
    const target =
      picker.querySelector(`.ncm-upsell__value[data-option-index="${firstOpen}"]:not([disabled])`) ||
      picker.querySelector('.ncm-upsell__value:not([disabled])');
    target?.focus({ preventScroll: true });
    picker.scrollIntoView({ block: 'nearest' });
  }

  rememberedCartSize() {
    const sizes = [...this.querySelectorAll('[data-ncm-line-size]')]
      .map((line) => line.dataset.ncmLineSize)
      .filter(Boolean);
    const unique = [...new Set(sizes)];
    return unique.length === 1 ? unique[0] : null;
  }

  initPicker(card) {
    if (card.ncmPicker) return;
    let data;
    try {
      data = JSON.parse(card.querySelector('[data-ncm-upsell-data]').textContent);
    } catch {
      return;
    }
    const sizeIndex = data.options.findIndex((name) => /size/i.test(name));
    const remembered = this.rememberedCartSize();
    const sizeAvailable =
      sizeIndex >= 0 &&
      Boolean(remembered) &&
      data.variants.some((variant) => variant.options[sizeIndex] === remembered);
    card.ncmPicker = {
      ...data,
      selected: data.defaults.map((value, index) => {
        if (index !== sizeIndex) return value;
        return sizeAvailable ? remembered : null;
      }),
    };
    this.renderPicker(card);
  }

  chooseUpsellValue(button) {
    const card = button.closest('[data-ncm-upsell]');
    if (!card?.ncmPicker || button.disabled) return;
    const { selected } = card.ncmPicker;
    const index = Number(button.dataset.optionIndex);
    selected[index] = button.dataset.value;
    this.renderPicker(card);
  }

  pickerVariant(card) {
    const picker = card.ncmPicker;
    if (!picker || picker.selected.some((value) => value == null)) return null;
    const variant = picker.variants.find((item) => item.options.every((value, i) => value === picker.selected[i]));
    return variant?.available ? variant : null;
  }

  renderPicker(card) {
    const { selected, variants, options } = card.ncmPicker;

    card.querySelectorAll('.ncm-upsell__value').forEach((button) => {
      const index = Number(button.dataset.optionIndex);
      const value = button.dataset.value;
      const exists = variants.some((variant) => variant.available && variant.options[index] === value);
      const fits = variants.some(
        (variant) =>
          variant.available &&
          variant.options[index] === value &&
          variant.options.every((other, i) => i === index || selected[i] == null || other === selected[i])
      );
      const isColor = button.classList.contains('ncm-color-swatch-label');
      button.disabled = !exists;
      button.classList.toggle('is-unavailable', exists && !fits);
      button.classList.toggle('ncm-color-swatch-label--oos', isColor && exists && !fits);
      button.setAttribute('aria-pressed', String(selected[index] === value));
    });
    card.querySelectorAll('[data-ncm-upsell-chosen]').forEach((label) => {
      label.textContent = selected[Number(label.dataset.ncmUpsellChosen)] || '';
    });

    const variant = this.pickerVariant(card);
    const confirm = card.querySelector('.ncm-upsell__picker [data-ncm-upsell-add]');
    confirm.dataset.label ??= confirm.textContent.trim();
    confirm.disabled = !variant;
    if (variant) {
      confirm.textContent = `${confirm.dataset.label} · ${variant.price}`;
    } else if (selected.some((value) => value == null)) {
      const missing = options[selected.findIndex((value) => value == null)] || options[0];
      const prompt = this.config.upsells?.selectPrompt || 'Select a {option}';
      confirm.textContent = prompt.replace('{option}', String(missing).toLowerCase());
    } else {
      confirm.textContent = confirm.dataset.label;
    }

    const matchesSelection = (item, ignoreSize = false) =>
      item.options.every((value, i) => {
        if (selected[i] == null) return true;
        if (ignoreSize && /size/i.test(options[i] || '')) return true;
        return value === selected[i];
      });
    const preview =
      variant || variants.find((item) => matchesSelection(item)) || variants.find((item) => matchesSelection(item, true));
    const image = card.querySelector('[data-ncm-upsell-image]');
    if (image && preview?.image) image.src = preview.image;

    const price = card.querySelector('[data-ncm-upsell-price]');
    if (price && preview?.price) {
      const amount = price.querySelector('span') || price;
      amount.textContent = preview.price;
      const compare = price.querySelector('s');
      if (compare) {
        compare.textContent = preview.compare || '';
        compare.hidden = !preview.compare;
      }
    }
  }

  addUpsell(button) {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
    const card = button.closest('[data-ncm-upsell]');
    const variantId = button.dataset.variantId || this.pickerVariant(card)?.id;
    if (!variantId) return;

    const error = card.querySelector('.ncm-upsell__error');
    if (error) error.hidden = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('loading');
    button.disabled = true;

    this.beginPricing({ announce: 'Updating cart totals' });
    return this.enqueue(async () => {
      const seq = this.nextSeq();
      try {
        const response = await fetch(`${routes.cart_add_url}.js`, {
          ...fetchConfig(),
          cache: 'no-store',
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: this.sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message || '');

        this.renderContents(state, { open: true, added: state.items || [], seq });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ncm-upsell', productVariantId: variantId, cartData: state });
      } catch (err) {
        if (!button.isConnected) return;
        button.removeAttribute('aria-busy');
        button.classList.remove('loading');
        button.disabled = false;
        if (error) {
          error.textContent = err.message || window.cartStrings?.error || '';
          error.hidden = false;
        }
      } finally {
        this.endPricing();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const node = parsed.querySelector(selector) || parsed.querySelector('.shopify-section');
    return node?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Delegated so the header icon keeps working after the header section re-renders (theme editor).
document.addEventListener('click', (event) => {
  const cartLink = event.target.closest('#cart-icon-bubble');
  const drawer = document.querySelector('cart-drawer');
  if (!cartLink || !drawer) return;
  event.preventDefault();
  drawer.openFromIcon(cartLink);
});
document.addEventListener('keydown', (event) => {
  const cartLink = event.target.closest?.('#cart-icon-bubble');
  if (!cartLink || event.code.toUpperCase() !== 'SPACE') return;
  event.preventDefault();
  document.querySelector('cart-drawer')?.openFromIcon(cartLink);
});
document.addEventListener('shopify:section:load', () => document.querySelector('cart-drawer')?.prepareCartIcon());

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (NCM_SELF_RENDERING_SOURCES.has(event.source)) return;
      this.closest('cart-drawer')?.refresh();
    });
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Drawer-quantity-${id}`);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.closest('cart-drawer')?.changeLine({ line, quantity: Number(quantity), name, variantId });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
